// this code was pulled out of old service manager

import { Logger } from "../../clients/logger";
import { System } from "../../common/system";
import { RouterClient } from "../../clients/routerClient";
import { DialogManagerClient } from "../../clients/dialogManagerClient";
import * as Constants from "../../common/constants";
import { FSBLDependencyManager } from "../../common/dependencyManager";
import { ApplicationState } from "./types";
import { MonitorInfoDetail } from "../../FEA";
import { getAllMonitors } from "../../common/Monitors";
import { isWindows } from "../../common/util";

export type RestartParams = {
	forceRestart?: boolean;
};

type Manifest = any;

/**
 * Manages an orderly shutdown of Finsemble
 */
class Shutdown {
	timeout: number;

	runningServices: string[] = [];

	// This flag gets set to true if the system has completed its graceful shutdown (all services have shutdown)
	hasShutdownGracefully: boolean = false;

	/**
	 * This will contain a function if we're waiting for graceful shutdown to complete. That function
	 * will be executed to resolve that wait.
	 */
	gracefulShutdownResolver: (() => void) | null = null;

	constructor(timeout: number) {
		this.timeout = timeout;
	}

	/**
	 * Returns a promise that resolves to true if the toolbar's claimed space has been released.
	 * This is only relevant on MS Windows where assimilation claims operating system space to make
	 * room for the toolbar. Sometimes assimilation can be slow to release that space, so we wait until
	 * that space is released before we shut down Finsemble (otherwise the space remains claimed even after Finsemble has stopped running).
	 *
	 * See this.waitForClaimedSpaceRelease() because we don't wait indefinitely.
	 *
	 * The calculations can be tested by calling this function early, before waitForGracefulShutdown resolves.
	 */
	async isAllClaimedSpaceIsReleased() {
		const monitors = await getAllMonitors();
		const doesClaimSpace = await isWindows();

		return !doesClaimSpace || monitors.every((monitor) => this.isMonitorReleased(monitor));
	}

	/**
	 * Returns true if our toolbar isn't "claiming space" on the selected monitor (which only occurs
	 * on MS Windows machines).
	 */
	isMonitorReleased(monitor: MonitorInfoDetail): boolean {
		const { taskbar } = monitor;

		if (!taskbar) return true;

		const shoudBeAvailableSurfaceArea =
			monitor.monitorRect.width * monitor.monitorRect.height - taskbar.width * taskbar.height;
		const actualAvailableSurfaceArea = monitor.availableRect.width * monitor.availableRect.height;

		// If the total monitor size less the taskbar size doesn't match the available space, then something (like our claimed space toolbar)
		// hasn't relinquished its space.
		return actualAvailableSurfaceArea === shoudBeAvailableSurfaceArea;
	}

	async waitForClaimedSpaceRelease() {
		Logger.system.debug("APPLICATION LIFECYCLE:SHUTDOWN: waiting for claimed space to be released.");
		let timeoutHandle;
		let loopHandle: any = null;

		// Times out after timeout seconds. This ends the race if the monitors have not yet been released.
		const timeoutPromise = new Promise<void>((resolve) => {
			timeoutHandle = setTimeout(() => {
				timeoutHandle = null;
				Logger.system.error("Monitors were not released within the allotted time. Was the system asleep?");
				resolve();
			}, this.timeout);
		});

		// Loops every 50 milliseconds waiting for claimed space to be released. This runs
		// indefinitely until the race ends
		const waitForClaimedSpaceToBeReleased = new Promise<void>((resolve) => {
			const loop = async () => {
				const isAllClaimedSpaceIsReleased = await this.isAllClaimedSpaceIsReleased();
				if (isAllClaimedSpaceIsReleased) {
					Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN: Claimed space has been released.");
					clearTimeout(loopHandle);
					loopHandle = null;
					resolve();
				} else {
					Logger.console.error("APPLICATION LIFECYCLE:SHUTDOWN: Claimed space not released. Checking again in 250ms.");
					loopHandle = setTimeout(loop, 250);
				}
			};
			loop();
		});

		await Promise.race([waitForClaimedSpaceToBeReleased, timeoutPromise]);
		if (timeoutHandle) clearTimeout(timeoutHandle);
		if (loopHandle) clearTimeout(loopHandle);
	}

	/**
	 * Signals that all active services have shut down.
	 */
	servicesHaveShutdown() {
		this.hasShutdownGracefully = true;
		if (this.gracefulShutdownResolver) this.gracefulShutdownResolver();
	}

	/**
	 * Sets this.gracefulShutdownResolver to be a Promise resolve for
	 * calls to await waitForGracefulShutdown();
	 */
	async waitForGracefulShutdown(forceRestart?: boolean) {
		Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN: waitForGracefulShutdown.");
		let timeoutHandle;

		const gracefulPromise = new Promise<void>((resolve) => {
			this.gracefulShutdownResolver = () => {
				Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN: graceful shutdown resolved.");
				resolve();
			};
		});

		if (forceRestart) {
			Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN: waitForGracefulShutdown. ForceRestart.");

			// Times out after timeout seconds. This ends the race if graceful shutdown hasn't been achieved.
			const timeoutPromise = new Promise<void>((resolve) => {
				timeoutHandle = setTimeout(() => {
					timeoutHandle = null;
					Logger.console.error("Timed out waiting for graceful shutdown. Forcing restart.");
					resolve();
				}, this.timeout);
			});

			Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN: Before Promise.race().", this.timeout);

			// If forceRestart is true then we race between the timeout and receiving a graceful shutdown
			await Promise.race([gracefulPromise, timeoutPromise]);

			Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN: After Promise.race().");

			if (timeoutHandle) clearTimeout(timeoutHandle);
		} else {
			Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN: waitForGracefulShutdown. Waiting (not ForceRestart).");
			// Otherwise we'll wait indefinitely for a graceful shutdown
			return gracefulPromise;
		}
	}

	/**
	 * Shuts down Finsemble except for this main window.
	 *
	 * If forceRestart is true, then graceful shutdown will be pre-empted after timeout seconds, otherwise
	 * it will wait indefinitely (such as for workspace save dialog)
	 */
	async shutdownEverythingButThisWindow(forceRestart?: boolean) {
		// First wait for services to shut down (for instance wait for the "Do you want to save this workspace" dialog to complete)
		if (!this.hasShutdownGracefully) {
			Logger.console.debug(
				"APPLICATION LIFECYCLE:SHUTDOWN:shutdownEverythingButThisWindow:Before waitForGracefulShutdown."
			);
			await this.waitForGracefulShutdown(forceRestart);
		} else {
			Logger.console.debug(
				"APPLICATION LIFECYCLE:SHUTDOWN:shutdownEverythingButThisWindow services are already shut down, proceeding to wait for claimed space."
			);
		}

		Logger.console.debug(
			"APPLICATION LIFECYCLE:SHUTDOWN:shutdownEverythingButThisWindow:Before waitForClaimedSpaceRelease."
		);
		// Then wait a set timeout period for the claimed space toolbar to release its claimed space
		await this.waitForClaimedSpaceRelease();
	}

	/**
	 * Publish a shutdown notification to all of Finsemble and then wait for services to shut down and claimed space to
	 * be released before we terminate the application.
	 */
	async transmitShutdownRequest() {
		// If shutdown is called before FEA has hid the splash screen then FEA will not shut down
		System.hideSplashScreen();
		Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN:transmitShutdownRequest:Sending out message to shut down.");

		RouterClient.publish<ApplicationState>(Constants.APPLICATION_STATE_CHANNEL, {
			state: "closing",
		});

		await this.shutdownEverythingButThisWindow();

		System.terminate();
	}

	/**
	 * Publish a shutdown notification to all of Finsemble and then wait for services to shut down and claimed space to
	 * be released before we restart the application.
	 *
	 * @param {*} [params.forceRestart] If forceRestart parameter is true, then force an immediate restart (but not before claimed space is released).
	 */
	async transmitRestartRequest(params?: RestartParams) {
		Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN:transmitRestartRequest:Sending out message to shut down.");
		Logger.console.debug("shutdownManager.transmitRestartRequest");
		RouterClient.publish<ApplicationState>(Constants.APPLICATION_STATE_CHANNEL, {
			state: "closing",
		});

		await this.shutdownEverythingButThisWindow(params?.forceRestart);

		System.restart();
	}
}

export class ShutdownManager {
	shutdown: Shutdown;

	mostRecentListOfServices: string[] = [];

	constructor(params: { manifest: Manifest }) {
		Logger.system.log("shutdownManager", params);
		const timeout: number = params.manifest.finsemble.shutdownTimeout ?? 10000;
		this.shutdown = new Shutdown(timeout);
		Logger.system.debug("Adding Listeners for shutdown and restart");

		RouterClient.addListener("Application.shutdown", () => {
			this.shutdown.transmitShutdownRequest();
		});

		RouterClient.addListener<RestartParams>("Application.restart", (error, message) => {
			// restart has an optional parameter to immediately force a shutdown. This is used
			// primarily by the "reset" UI component
			this.shutdown.transmitRestartRequest(message.data);
		});

		System.addEventListener("apply-update-requested", () => {
			// Show dialog to request restart
			Logger.system.log("Apply update requested: after onReady");
			DialogManagerClient.open(
				"yesNo",
				{
					monitor: "primary",
					title: "Application Update",
					question:
						"A new version has been downloaded. Do you want to restart the application now to apply the updates?",
					showTimer: false,
					showNegativeButton: false,
					affirmativeResponseLabel: "Restart Now",
				},
				async (err, response) => {
					if (response.choice !== "cancel") {
						// if yes restart
						RouterClient.transmit("Application.restart", {});
					}
				}
			);
		});
	}

	/**
	 * This function is invoked for each completion stage by the bootEngine. Each time, the passed `activeServices` argument
	 * contains a more complete list of all active services. This function registers with the dependency manager for shutdown
	 * notification, and when all services have shut down it signals Shutdown that it is safe to quit.
	 */
	public addActiveServicesToDependencyManager(activeServices: string[]) {
		/** *
		 * We filter the routerService because of a complex series of race conditions unique to the router service.
		 * The routerService does not use the base service, but it does use the dependency manager.
		 * The dependency manager uses the routerClient to communicate with the ServiceManager to figure out when services come online and when they go offline.
		 * The dependency manager has logic in it to set services offline if it previously knew that the service was online.
		 * Because the dependency manager inside of the routerService does not get the initial list of
		 * online services, it's unable to use the dependency manager to defer action on shutdown.
		 * What this means practically is that the routerService times out after 10 seconds, because it's waiting for the LoggerService to shut down. The Logger will never shut down as far as the
		 * routerService is concerned.
		 *
		 */

		// Save our services variable as a closure (see below)
		const services = activeServices.filter((name) => name !== "routerService");
		// Replace the list of services with the most recent. Each time, the list will be the latest list of all active services.
		this.mostRecentListOfServices = services;

		Logger.console.debug(
			"shutdownManager.addActiveServicesToDependencyManager: Will wait for the following services at shutdown",
			services
		);

		// Called multiple times
		const servicesHaveShutdown = () => {
			// Only when the services object closure is identical to the most recent list do we know that it's safe to shut down
			Logger.console.debug(
				"APPLICATION LIFECYCLE:SHUTDOWN:servicesHaveShutdown",
				services,
				this.mostRecentListOfServices
			);

			if (services === this.mostRecentListOfServices) {
				Logger.console.debug("APPLICATION LIFECYCLE:SHUTDOWN:servicesHaveShutdown DONE");
				this.shutdown.servicesHaveShutdown();
			}
		};

		// Listens for when services signal that they have shutdown. It's okay to construct many dependency managers.
		const dependencyManager = new FSBLDependencyManager();
		dependencyManager.shutdown.waitFor({ services }, servicesHaveShutdown);
	}
}
